import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Clock, ArrowRight, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ApiError } from '../api/http';
import * as usersApi from '../api/users';

export function TrialBanner() {
  const { token, user } = useAuth();
  const [requesting, setRequesting] = useState(false);
  const [trial, setTrial] = useState<usersApi.TrialCredentials | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || !user) return;
      try {
        const creds = await usersApi.getTrialCredentials(token);
        if (!cancelled) setTrial(creds);
      } catch (e) {
        // no trial account yet -> hide banner
        if (!cancelled) setTrial(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user]);

  if (!user || !token || !trial) return null;

  const isExpired = !trial.isActive;
  const daysLeft = Math.ceil((new Date(trial.trialEndedAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  const showActiveWarning = trial.isActive && daysLeft <= 3 && daysLeft >= 0;
  const canExtendTrial = (trial.trialDays ?? 0) < 28;

  const handleRequestExtension = async () => {
    setRequesting(true);
    try {
      if (!canExtendTrial) return;
      const extended = await usersApi.extendTrial(token);
      setTrial(extended);
    } catch (error) {
      if (error instanceof ApiError) console.error('Extend trial failed:', error.message, error.payload);
      else console.error('Extend trial failed:', error);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <AnimatePresence>
      {(isExpired || showActiveWarning) && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className={`relative z-100 w-full text-white py-2 px-4 text-center text-sm font-medium flex flex-wrap items-center justify-center gap-x-4 gap-y-2 ${
            isExpired ? 'bg-red-600' : 'bg-amber-500'
          }`}
        >
          {isExpired ? (
            <>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span>Your free trial has expired. Please upgrade or request an extension.</span>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/pricing" className="underline font-bold flex items-center gap-1 hover:text-white/80 transition-colors">
                  Upgrade Now <ArrowRight className="w-3 h-3" />
                </Link>
                <button 
                  onClick={handleRequestExtension}
                  disabled={requesting || !canExtendTrial}
                  className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {requesting ? 'Sending...' : (
                    <>
                      <Send className="w-3 h-3" />
                      {canExtendTrial ? 'Extend Trial' : 'Extend limit reached'}
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Your free trial ends in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}.</span>
              </div>
              <Link to="/pricing" className="underline font-bold flex items-center gap-1 hover:text-white/80 transition-colors">
                Upgrade Now <ArrowRight className="w-3 h-3" />
              </Link>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
